import { Capacitor } from '@capacitor/core';

const DRIVE_SCOPE = 'https://www.googleapis.com/auth/drive.appdata';
const DRIVE_FILENAME = 'rethread-backup.enc.json';
const GIS_SRC = 'https://accounts.google.com/gsi/client';

type TokenResponse = {
  access_token?: string;
  expires_in?: number;
  error?: string;
  error_description?: string;
};

type DriveFileMeta = {
  id: string;
  modifiedTime: string;
};

type GoogleOauth2 = {
  initTokenClient: (config: {
    client_id: string;
    scope: string;
    callback: (response: TokenResponse) => void;
    error_callback?: (error: unknown) => void;
  }) => {
    requestAccessToken: (options?: { prompt?: '' | 'consent' | 'select_account' }) => void;
  };
};

let gisPromise: Promise<void> | null = null;
let accessToken: string | null = null;
let accessTokenExpiresAt = 0;

export function hasGoogleDriveClientId(): boolean {
  if (Capacitor.isNativePlatform()) {
    return Boolean(import.meta.env.VITE_GOOGLE_DRIVE_NATIVE_CLIENT_ID && import.meta.env.VITE_GOOGLE_DRIVE_NATIVE_REDIRECT_URI);
  }
  return Boolean(import.meta.env.VITE_GOOGLE_DRIVE_CLIENT_ID);
}

export function isGoogleDriveConnected(): boolean {
  return Boolean(accessToken) && Date.now() < accessTokenExpiresAt;
}

export function disconnectGoogleDrive(): void {
  accessToken = null;
  accessTokenExpiresAt = 0;
}

export async function connectGoogleDrive(): Promise<void> {
  await ensureAccessToken(true);
}

export async function uploadBackupToGoogleDrive(content: string): Promise<void> {
  const token = await ensureAccessToken(true);
  const existing = await findLatestBackup(token);
  if (existing) {
    await upsertBackup(token, content, existing.id);
    return;
  }
  await upsertBackup(token, content, null);
}

export async function downloadLatestBackupFromGoogleDrive(): Promise<string> {
  const token = await ensureAccessToken(true);
  const file = await findLatestBackup(token);
  if (!file) {
    throw new Error('No Google Drive backup found yet.');
  }
  const res = await fetch(`https://www.googleapis.com/drive/v3/files/${encodeURIComponent(file.id)}?alt=media`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  if (!res.ok) {
    throw new Error('Could not download backup from Google Drive.');
  }
  return res.text();
}

async function ensureAccessToken(interactive: boolean): Promise<string> {
  if (!hasGoogleDriveClientId()) {
    throw new Error(
      Capacitor.isNativePlatform()
        ? 'Google Drive is not configured. Set VITE_GOOGLE_DRIVE_NATIVE_CLIENT_ID and VITE_GOOGLE_DRIVE_NATIVE_REDIRECT_URI.'
        : 'Google Drive is not configured. Set VITE_GOOGLE_DRIVE_CLIENT_ID.'
    );
  }
  if (accessToken && Date.now() < accessTokenExpiresAt) {
    return accessToken;
  }
  if (Capacitor.isNativePlatform()) {
    const token = await requestNativeAccessToken(interactive);
    accessToken = token.accessToken;
    accessTokenExpiresAt = token.expiresAt;
    return token.accessToken;
  }
  await loadGisScript();
  const oauth2 = window.google?.accounts?.oauth2;
  if (!oauth2) {
    throw new Error('Google sign-in library failed to load.');
  }
  const token = await requestToken(oauth2, interactive);
  accessToken = token.accessToken;
  accessTokenExpiresAt = token.expiresAt;
  return token.accessToken;
}

async function loadGisScript(): Promise<void> {
  if (window.google?.accounts?.oauth2) return;
  if (gisPromise) return gisPromise;
  gisPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${GIS_SRC}"]`) as HTMLScriptElement | null;
    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('Failed to load Google sign-in script.')), { once: true });
      return;
    }
    const script = document.createElement('script');
    script.src = GIS_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Google sign-in script.'));
    document.head.appendChild(script);
  });
  return gisPromise;
}

async function requestNativeAccessToken(interactive: boolean): Promise<{ accessToken: string; expiresAt: number }> {
  if (!interactive) {
    throw new Error('Google Drive authorization required.');
  }
  const clientId = String(import.meta.env.VITE_GOOGLE_DRIVE_NATIVE_CLIENT_ID ?? '');
  const redirectUri = String(import.meta.env.VITE_GOOGLE_DRIVE_NATIVE_REDIRECT_URI ?? '');
  if (!clientId || !redirectUri) {
    throw new Error('Missing native Google OAuth configuration.');
  }

  const [{ Browser }, { App }] = await Promise.all([import('@capacitor/browser'), import('@capacitor/app')]);
  const codeVerifier = randomUrlSafe(64);
  const codeChallenge = await pkceChallenge(codeVerifier);
  const state = randomUrlSafe(24);

  const authUrl = new URL('https://accounts.google.com/o/oauth2/v2/auth');
  authUrl.searchParams.set('client_id', clientId);
  authUrl.searchParams.set('redirect_uri', redirectUri);
  authUrl.searchParams.set('response_type', 'code');
  authUrl.searchParams.set('scope', DRIVE_SCOPE);
  authUrl.searchParams.set('state', state);
  authUrl.searchParams.set('code_challenge', codeChallenge);
  authUrl.searchParams.set('code_challenge_method', 'S256');
  authUrl.searchParams.set('access_type', 'offline');
  authUrl.searchParams.set('prompt', 'consent');

  return new Promise((resolve, reject) => {
    let finished = false;
    let listener: { remove: () => Promise<void> } | null = null;
    const timeout = window.setTimeout(() => {
      if (finished) return;
      finished = true;
      void listener?.remove();
      void Browser.close().catch(() => undefined);
      reject(new Error('Google sign-in timed out.'));
    }, 180000);

    const cleanup = async (): Promise<void> => {
      window.clearTimeout(timeout);
      if (listener) await listener.remove();
      await Browser.close().catch(() => undefined);
    };

    void App.addListener('appUrlOpen', async ({ url }) => {
      if (finished) return;
      if (!url.startsWith(redirectUri)) return;
      finished = true;
      try {
        await cleanup();
        const callback = new URL(url);
        const returnedState = callback.searchParams.get('state');
        const code = callback.searchParams.get('code');
        const error = callback.searchParams.get('error');
        if (error) throw new Error(`Google authorization failed: ${error}`);
        if (!code) throw new Error('Google authorization returned no code.');
        if (!returnedState || returnedState !== state) throw new Error('Google authorization state mismatch.');

        const tokenResponse = await fetch('https://oauth2.googleapis.com/token', {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams({
            client_id: clientId,
            code,
            code_verifier: codeVerifier,
            redirect_uri: redirectUri,
            grant_type: 'authorization_code'
          })
        });
        if (!tokenResponse.ok) {
          const body = await tokenResponse.text();
          throw new Error(`Google token exchange failed (${tokenResponse.status}): ${body.slice(0, 180)}`);
        }
        const data = (await tokenResponse.json()) as { access_token?: string; expires_in?: number };
        if (!data.access_token) throw new Error('Google token exchange returned no access token.');
        const ttlSec = Number(data.expires_in ?? 0);
        resolve({
          accessToken: data.access_token,
          expiresAt: Date.now() + Math.max(60, ttlSec - 30) * 1000
        });
      } catch (error) {
        reject(error instanceof Error ? error : new Error('Google sign-in failed.'));
      }
    })
      .then((handle) => {
        listener = handle;
        return Browser.open({ url: authUrl.toString() });
      })
      .catch((error) => {
        finished = true;
        window.clearTimeout(timeout);
        reject(error instanceof Error ? error : new Error('Failed to open browser for Google sign-in.'));
      });
  });
}

async function requestToken(oauth2: GoogleOauth2, interactive: boolean): Promise<{ accessToken: string; expiresAt: number }> {
  const clientId = import.meta.env.VITE_GOOGLE_DRIVE_CLIENT_ID as string;
  return new Promise((resolve, reject) => {
    const tokenClient = oauth2.initTokenClient({
      client_id: clientId,
      scope: DRIVE_SCOPE,
      callback: (response) => {
        if (response.error || !response.access_token) {
          reject(new Error(response.error_description || 'Google Drive authorization failed.'));
          return;
        }
        const ttlSec = Number(response.expires_in ?? 0);
        resolve({
          accessToken: response.access_token,
          expiresAt: Date.now() + Math.max(60, ttlSec - 30) * 1000
        });
      },
      error_callback: () => reject(new Error('Google Drive authorization failed.'))
    });
    tokenClient.requestAccessToken({ prompt: interactive ? 'consent' : '' });
  });
}

function randomUrlSafe(length: number): string {
  const bytes = crypto.getRandomValues(new Uint8Array(length));
  return toBase64Url(bytes);
}

async function pkceChallenge(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return toBase64Url(new Uint8Array(digest));
}

function toBase64Url(bytes: Uint8Array): string {
  let binary = '';
  const chunkSize = 8192;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

async function findLatestBackup(token: string): Promise<DriveFileMeta | null> {
  const query = encodeURIComponent(`name='${DRIVE_FILENAME}' and 'appDataFolder' in parents and trashed=false`);
  const url =
    `https://www.googleapis.com/drive/v3/files?q=${query}` +
    '&spaces=appDataFolder&orderBy=modifiedTime desc&pageSize=1&fields=files(id,modifiedTime)';
  const res = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` }
  });
  if (!res.ok) {
    throw new Error('Could not list Google Drive backups.');
  }
  const data = (await res.json()) as { files?: DriveFileMeta[] };
  return data.files?.[0] ?? null;
}

async function upsertBackup(token: string, content: string, fileId: string | null): Promise<void> {
  const boundary = `friction-boundary-${Math.random().toString(36).slice(2)}`;
  const metadata = fileId
    ? { name: DRIVE_FILENAME }
    : {
        name: DRIVE_FILENAME,
        parents: ['appDataFolder']
      };
  const body =
    `--${boundary}\r\n` +
    'Content-Type: application/json; charset=UTF-8\r\n\r\n' +
    `${JSON.stringify(metadata)}\r\n` +
    `--${boundary}\r\n` +
    'Content-Type: application/json\r\n\r\n' +
    `${content}\r\n` +
    `--${boundary}--`;

  const endpoint = fileId
    ? `https://www.googleapis.com/upload/drive/v3/files/${encodeURIComponent(fileId)}?uploadType=multipart`
    : 'https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart';
  const method = fileId ? 'PATCH' : 'POST';

  const res = await fetch(endpoint, {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': `multipart/related; boundary=${boundary}`
    },
    body
  });

  if (!res.ok) {
    throw new Error('Could not upload backup to Google Drive.');
  }
}
